// The funnel state has to survive a refresh. Without this, reloading any step
// left `insuranceType` empty, the route guard bounced the agent to the home
// page, and the in-progress case was lost.
const readSession = (key) => {
  try {
    return sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeSession = (key, value) => {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    // Private mode / blocked storage: state still works for this page view.
  }
};

const readAgentName = () => {
  try {
    return sessionStorage.getItem("agent_name") || null;
  } catch {
    return null;
  }
};

const readAgentId = () => {
  try {
    const raw = sessionStorage.getItem("currentAgentId");
    if (!raw || raw === "undefined") return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const initialState = {
  agent: {
    currentAgentId: readAgentId(),
    agentType: null,
    agentName: readAgentName(),
  },
  insuranceType: readSession("insuranceType"),
  extractedOcrData: null,
  caseId: readSession("caseId"),
  // Persisted: the quote screens need it after a refresh, and it is answered
  // several steps earlier.
  travelPlanningType: readSession("travelPlanningType"),
  petType: "",
  planName: "",
  // { product: "pet" | "travel" | ..., schemes: NormalizedPlan[] }
  compare: { product: null, schemes: [] },
  // { [journeyKey]: furthestStepIndexReached } — how far the funnel has got.
  journeyProgress: {},
  // { [journeyKey]: { fieldName: value } } — every answer given so far, which
  // is what the navigation ribbon shows and lets you edit in place.
  journeyAnswers: {},
};

const rootReducer = (state = initialState, action) => {
  switch (action.type) {
    case "CLEAR_AUTH":
      try {
        sessionStorage.removeItem("currentAgentId");
        sessionStorage.removeItem("agent_name");
        sessionStorage.removeItem("insuranceType");
        sessionStorage.removeItem("caseId");
        sessionStorage.removeItem("travelPlanningType");
      } catch {}
      return {
        ...state,
        agent: { ...state.agent, currentAgentId: null, agentType: null, agentName: null },
        insuranceType: "",
        caseId: "",
      };
    case "SET_AGENT_ID":
      sessionStorage.setItem("currentAgentId", JSON.stringify(action.payload));
      return {
        ...state,
        agent: {
          ...state.agent,
          currentAgentId: action.payload === null ? null : action.payload,
        },
      };
    case "SET_AGENT_NAME":
      try { sessionStorage.setItem("agent_name", action.payload || ""); } catch {}
      return { ...state, agent: { ...state.agent, agentName: action.payload } };
    case "SET_AGENT_TYPE":
      return { ...state, agent: { ...state.agent, agentType: action.payload } };
    case "SET_INSURANCE_TYPE": {
      // Switching product restarts that funnel, so its progress must not
      // carry over from an abandoned attempt.
      const next = { ...state.journeyProgress };
      delete next[action.payload];
      writeSession("insuranceType", action.payload);
      return { ...state, insuranceType: action.payload, journeyProgress: next };
    }
    case "SET_CASE_ID":
      writeSession("caseId", action.payload);
      return { ...state, caseId: action.payload };
    case "SET_OCR_DATA":
      return { ...state, extractedOcrData: action.payload };
    case "SET_PLAN_NAME":
      return { ...state, planName: action.payload };
    case "SET_TRAVEL_PLANNING_TYPE":
      writeSession("travelPlanningType", action.payload || "");
      return { ...state, travelPlanningType: action.payload };
    case "SET_PET_TYPE":
      return { ...state, petType: action.payload };
    case "SET_COMPARE_SCHEMES":
      return {
        ...state,
        compare: {
          product: action.payload.product,
          schemes: action.payload.schemes,
        },
      };
    case "CLEAR_COMPARE_SCHEMES":
      return { ...state, compare: { product: null, schemes: [] } };
    case "MARK_STEP_REACHED": {
      const { journey, index } = action.payload;
      const current = state.journeyProgress[journey] ?? -1;
      // Monotonic: navigating back must not shrink how far the user has got,
      // or the steps ahead of them would stop being clickable.
      if (index <= current) return state;
      return {
        ...state,
        journeyProgress: { ...state.journeyProgress, [journey]: index },
      };
    }
    case "RECORD_ANSWERS": {
      const { journey, values } = action.payload;
      return {
        ...state,
        journeyAnswers: {
          ...state.journeyAnswers,
          [journey]: { ...(state.journeyAnswers[journey] || {}), ...values },
        },
      };
    }
    case "RESET_JOURNEY_PROGRESS": {
      const next = { ...state.journeyProgress };
      delete next[action.payload.journey];
      const answers = { ...state.journeyAnswers };
      delete answers[action.payload.journey];
      return { ...state, journeyProgress: next, journeyAnswers: answers };
    }
    default:
      return state;
  }
};

export default rootReducer;
